import React, { useCallback, useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Bell, CheckCircle2, BarChart3, FileDown, ClipboardCheck, ChevronDown, ChevronUp } from "lucide-react";
import { exportCombinedMonthlyPDF } from "@/lib/pdf";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid,
} from "recharts";

const AR_MONTHS = ["يناير","فبراير","مارس","أبريل","مايو","يونيو","يوليو","أغسطس","سبتمبر","أكتوبر","نوفمبر","ديسمبر"];
const COLORS = ["#3E6A56", "#C97052", "#DDA74A", "#5B7DB1", "#8A5BA6", "#B84B4B", "#4A9B8E"];

const fmtMonth = (m) => {
  const [y, mo] = m.split("-");
  return `${AR_MONTHS[parseInt(mo, 10) - 1]} ${y}`;
};

export function NotificationsPanel() {
  const [notifs, setNotifs] = useState([]);

  const load = async () => {
    try { const { data } = await api.get("/admin/notifications"); setNotifs(data); }
    catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const ack = async (n) => {
    try {
      await api.post(`/admin/notifications/${n.id}/ack`);
      toast.success("تم تأكيد استلام التقرير");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const unread = notifs.filter((n) => !n.read);
  if (notifs.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl border border-[#e6e2d8] shadow-[0_8px_30px_rgb(0,0,0,0.03)] p-5" data-testid="notifications-panel">
      <div className="flex items-center gap-2 mb-4">
        <div className="relative">
          <Bell className="w-5 h-5 text-[#3E6A56]" />
          {unread.length > 0 && (
            <span className="absolute -top-1.5 -end-1.5 w-4 h-4 rounded-full bg-[#B84B4B] text-white text-[10px] flex items-center justify-center" data-testid="unread-count-badge">
              {unread.length}
            </span>
          )}
        </div>
        <h3 className="font-bold text-[#1A201C]" style={{ fontFamily: "IBM Plex Sans Arabic" }}>التقارير المرسلة من المرشدين</h3>
      </div>
      <div className="space-y-2 max-h-72 overflow-y-auto">
        {notifs.slice(0, 10).map((n) => (
          <div key={n.id} className={`flex items-center justify-between gap-3 rounded-xl border p-3 ${n.read ? "border-[#f0ebe0] bg-[#F9F8F6]/50" : "border-[#3E6A56]/30 bg-[#3E6A56]/5"}`} data-testid={`notification-${n.id}`}>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[#1A201C]">
                تقرير شهري{n.month ? ` — ${n.month}` : ""} من {n.counselor_name}
              </div>
              <div className="text-xs text-[#4A554E] mt-0.5">
                {new Date(n.created_at).toLocaleString("ar-EG", { dateStyle: "medium", timeStyle: "short" })}
              </div>
            </div>
            {n.read ? (
              <span className="flex items-center gap-1 text-xs text-[#3E6A56] shrink-0"><CheckCircle2 className="w-4 h-4" /> تم الاستلام</span>
            ) : (
              <Button size="sm" onClick={() => ack(n)} className="rounded-xl bg-[#3E6A56] hover:bg-[#2D4F40] shrink-0" data-testid={`ack-notification-${n.id}`}>
                تأكيد الاستلام
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function CombinedMonthlyExport() {
  const [month, setMonth] = useState(AR_MONTHS[new Date().getMonth()]);
  const [busy, setBusy] = useState(false);

  const doExport = async () => {
    setBusy(true);
    try {
      const [recs, users] = await Promise.all([
        api.get("/records", { params: { type: "monthly_report" } }),
        api.get("/users"),
      ]);
      const usersById = Object.fromEntries(users.data.map((u) => [u.id, u]));
      const reports = recs.data
        .filter((r) => r.data?.month === month)
        .map((r) => ({
          counselor_name: usersById[r.counselor_id]?.name || "—",
          school_key: usersById[r.counselor_id]?.school_key,
          school_name: usersById[r.counselor_id]?.school_name || r.data.school,
          gender: usersById[r.counselor_id]?.gender,
          data: r.data,
        }));
      if (reports.length === 0) { toast.error(`لا توجد تقارير شهرية لشهر ${month}`); return; }
      await exportCombinedMonthlyPDF(month, reports);
      toast.success(`تم تصدير ${reports.length} تقرير لشهر ${month}`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#e6e2d8] shadow-[0_8px_30px_rgb(0,0,0,0.03)] p-5" data-testid="combined-export-card">
      <div className="flex items-center gap-2 mb-4">
        <FileDown className="w-5 h-5 text-[#3E6A56]" />
        <h3 className="font-bold text-[#1A201C]" style={{ fontFamily: "IBM Plex Sans Arabic" }}>تصدير شامل للتقارير الشهرية</h3>
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="rounded-xl bg-white border-[#e6e2d8] sm:w-52" data-testid="combined-export-month-select"><SelectValue /></SelectTrigger>
          <SelectContent>{AR_MONTHS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={doExport} disabled={busy} className="rounded-xl bg-[#3E6A56] hover:bg-[#2D4F40]" data-testid="combined-export-button">
          <FileDown className="w-4 h-4 me-2" /> {busy ? "جاري التصدير..." : "تصدير PDF مجمّع"}
        </Button>
      </div>
      <p className="text-xs text-[#4A554E] mt-3">يجمع تقارير كل المرشدين للشهر المحدد في ملف PDF واحد بترويسة كيان.</p>
    </div>
  );
}

export function MonthlyActivityChart() {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get("/admin/monthly-activity")
      .then((r) => setRows(r.data))
      .catch((e) => toast.error(formatErr(e)));
  }, []);

  if (rows.length === 0) return null;

  const months = [...new Set(rows.map((r) => r.month))].sort();
  const names = [...new Set(rows.map((r) => r.name))];
  const data = months.map((m) => {
    const o = { month: fmtMonth(m) };
    rows.filter((r) => r.month === m).forEach((r) => { o[r.name] = (o[r.name] || 0) + r.count; });
    return o;
  });

  return (
    <div className="bg-white rounded-2xl border border-[#e6e2d8] shadow-[0_8px_30px_rgb(0,0,0,0.03)] p-5" data-testid="monthly-activity-chart">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="w-5 h-5 text-[#3E6A56]" />
        <h3 className="font-bold text-[#1A201C]" style={{ fontFamily: "IBM Plex Sans Arabic" }}>نشاط المرشدين الشهري (عدد السجلات)</h3>
      </div>
      <div className="h-72" dir="ltr">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0ebe0" />
            <XAxis dataKey="month" tick={{ fontSize: 12, fontFamily: "Tajawal" }} reversed />
            <YAxis allowDecimals={false} tick={{ fontSize: 12 }} orientation="right" />
            <Tooltip contentStyle={{ direction: "rtl", fontFamily: "Tajawal", borderRadius: 12, border: "1px solid #e6e2d8" }} />
            <Legend wrapperStyle={{ fontFamily: "Tajawal", fontSize: 12, direction: "rtl" }} />
            {names.map((n, i) => (
              <Bar key={n} dataKey={n} fill={COLORS[i % COLORS.length]} radius={[6, 6, 0, 0]} maxBarSize={34} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const EVALUATION_COLORS = ["#3E6A56", "#C97052", "#DDA74A", "#5B7DB1", "#8A5BA6", "#4A9B8E"];
const currentAcademicYear = () => {
  const now = new Date();
  return now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
};
const asDate = (date) => date.toISOString().slice(0, 10);
const monthRange = (month) => {
  const [year, monthNumber] = month.split("-").map(Number);
  if (!year || !monthNumber) return null;
  return {
    start: `${month}-01`,
    end: asDate(new Date(Date.UTC(year, monthNumber, 0))),
  };
};
const academicRange = (year, term) => {
  if (term === "year") return { start: `${year}-09-01`, end: `${Number(year) + 1}-08-31` };
  return term === "first"
    ? { start: `${year}-09-01`, end: `${Number(year) + 1}-01-31` }
    : { start: `${Number(year) + 1}-02-01`, end: `${Number(year) + 1}-08-31` };
};
const RECORD_LABELS = {
  academic_record: "السجل الأكاديمي",
  counseling_case: "حالة إرشادية", individual_session: "جلسة إرشاد فردي",
  special_case: "حالة خاصة", info_form: "دراسة حالة", gifted_student: "سجل موهوب",
  group_session: "جلسة إرشاد جمعي", guidance_lesson: "حصة إرشادية",
  radio_broadcast: "إذاعة إرشادية", program: "برنامج", daily_incident: "موقف يومي",
  behavior_contract: "تعاقد سلوكي", absence: "متابعة غياب",
  parent_contact: "تواصل مع ولي أمر", monthly_report: "تقرير شهري",
  class_visit: "زيارة صفية",
};

export function CounselorEvaluation() {
  const today = asDate(new Date());
  const [period, setPeriod] = useState("month");
  const [month, setMonth] = useState(today.slice(0, 7));
  const [year, setYear] = useState(String(currentAcademicYear()));
  const [term, setTerm] = useState("first");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [basis, setBasis] = useState("");
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({});
  const years = Array.from({ length: 6 }, (_, index) => currentAcademicYear() - index);

  const load = useCallback(async () => {
    let range;
    if (period === "month") range = monthRange(month);
    else if (period === "custom") range = { start: startDate, end: endDate };
    else range = academicRange(year, period === "year" ? "year" : term);
    if (!range?.start || !range?.end || range.start > range.end) {
      toast.error("تحقق من تاريخ بداية الفترة ونهايتها");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.get("/admin/counselor-evaluation", {
        params: { start_date: range.start, end_date: range.end },
      });
      setRows(data.counselors);
      setCategories(data.categories);
      setBasis(data.score_basis);
      setExpanded({});
    } catch (error) {
      toast.error(formatErr(error));
    } finally {
      setBusy(false);
    }
  }, [endDate, month, period, startDate, term, year]);

  useEffect(() => { load(); }, [load]);

  const chartData = rows.map((row) => ({
    name: row.name,
    "متابعة الحالات والطلاب": row.breakdown.find((item) => item.key === "student_followup")?.count || 0,
    "الإرشاد الجماعي والبرامج": row.breakdown.find((item) => item.key === "group_guidance")?.count || 0,
    "دعم السلوك": row.breakdown.find((item) => item.key === "behavior_support")?.count || 0,
    "متابعة الحضور": row.breakdown.find((item) => item.key === "attendance_followup")?.count || 0,
    "التواصل مع الأسرة": row.breakdown.find((item) => item.key === "family_communication")?.count || 0,
    "التخطيط والتقارير": row.breakdown.find((item) => item.key === "planning_and_reports")?.count || 0,
  }));
  const activeCategories = categories.filter((item) => item.available);

  return (
    <section className="space-y-5 rounded-2xl border border-[#e6e2d8] bg-white p-5 shadow-[0_8px_30px_rgb(0,0,0,0.03)]" data-testid="counselor-evaluation">
      <div className="flex items-center gap-2">
        <ClipboardCheck className="h-5 w-5 text-[#3E6A56]" />
        <div>
          <h3 className="font-bold text-[#1A201C]" style={{ fontFamily: "IBM Plex Sans Arabic" }}>تقييم أعمال المرشدين الطلابيين</h3>
          <p className="mt-1 text-xs text-[#4A554E]">اختر الفترة لعرض الأعمال المسجلة ومؤشر الإنجاز لجميع المرشدين ضمن صلاحيتك.</p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-44">
          <label className="mb-1 block text-sm font-medium">نوع الفترة</label>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger data-testid="evaluation-period"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="custom">من تاريخ إلى تاريخ</SelectItem>
              <SelectItem value="month">شهر كامل</SelectItem>
              <SelectItem value="semester">فصل دراسي</SelectItem>
              <SelectItem value="year">عام دراسي</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {period === "custom" && (
          <>
            <label className="text-sm">من <Input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} /></label>
            <label className="text-sm">إلى <Input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} /></label>
          </>
        )}
        {period === "month" && (
          <label className="text-sm">الشهر <Input type="month" value={month} onChange={(event) => setMonth(event.target.value)} /></label>
        )}
        {(period === "semester" || period === "year") && (
          <label className="min-w-44 text-sm">العام الدراسي
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{years.map((value) => <SelectItem key={value} value={String(value)}>{value}–{value + 1}</SelectItem>)}</SelectContent>
            </Select>
          </label>
        )}
        {period === "semester" && (
          <label className="min-w-40 text-sm">الفصل
            <Select value={term} onValueChange={setTerm}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="first">الفصل الأول (سبتمبر–يناير)</SelectItem>
                <SelectItem value="second">الفصل الثاني (فبراير–أغسطس)</SelectItem>
              </SelectContent>
            </Select>
          </label>
        )}
        <Button onClick={load} disabled={busy} className="rounded-xl bg-[#3E6A56] hover:bg-[#2D4F40]" data-testid="evaluation-load">
          {busy ? "جاري إعداد التقييم..." : "عرض التقييم"}
        </Button>
      </div>

      <div className="rounded-xl bg-[#F9F8F6] p-3 text-xs leading-6 text-[#4A554E]">
        <strong>طريقة احتساب المؤشر:</strong> أوزان الفئات هي {categories.map((item) => `${item.label} ${item.weight}%`).join("، ")}.
        {basis && <span> الدرجة نسبية إلى أعلى عدد أعمال مسجل في الفئة نفسها خلال الفترة؛ وهي مؤشر توثيق ونشاط، وليست حكمًا آليًا على جودة العمل. تظهر أوزان الفئات التي لها أعمال فقط.</span>}
        {!basis && <span> الدرجات تُحسب بعد تحميل الفترة.</span>}
      </div>

      {chartData.length > 0 && (
        <div className="h-80" dir="ltr" data-testid="evaluation-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 50 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0ebe0" />
              <XAxis dataKey="name" interval={0} angle={-25} textAnchor="end" tick={{ fontSize: 11, fontFamily: "Tajawal" }} />
              <YAxis allowDecimals={false} orientation="right" />
              <Tooltip contentStyle={{ direction: "rtl", fontFamily: "Tajawal", borderRadius: 12, border: "1px solid #e6e2d8" }} />
              <Legend wrapperStyle={{ fontFamily: "Tajawal", fontSize: 11, direction: "rtl" }} />
              {activeCategories.map((category, index) => (
                <Bar key={category.key} dataKey={category.label} stackId="work" fill={EVALUATION_COLORS[index % EVALUATION_COLORS.length]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="space-y-3">
        {rows.map((row) => (
          <article key={row.id} className="rounded-xl border border-[#e6e2d8] p-4" data-testid={`evaluation-counselor-${row.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h4 className="font-bold text-[#1A201C]">{row.name}</h4>
                <p className="text-xs text-[#4A554E]">{row.school_name || row.username} · {row.total_records} عمل موثق · {row.rating}</p>
              </div>
              <div className="text-center">
                <strong className="text-2xl text-[#3E6A56]">{row.score}</strong><span className="text-sm text-[#4A554E]"> / 100</span>
              </div>
              <Button variant="outline" size="sm" onClick={() => setExpanded((current) => ({ ...current, [row.id]: !current[row.id] }))}>
                {expanded[row.id] ? <ChevronUp className="me-1 h-4 w-4" /> : <ChevronDown className="me-1 h-4 w-4" />}
                {expanded[row.id] ? "إخفاء الأعمال" : "عرض تفاصيل الأعمال"}
              </Button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {row.breakdown.filter((item) => item.count > 0).map((item) => (
                <span key={item.key} className="rounded-lg bg-[#F9F8F6] px-2 py-1 text-xs">
                  {item.label}: {item.count} ({item.points} من {item.weight})
                </span>
              ))}
            </div>
            {expanded[row.id] && (
              <div className="mt-4 max-h-80 space-y-2 overflow-y-auto border-t border-[#e6e2d8] pt-3">
                {row.activities.length ? row.activities.map((activity) => (
                  <div key={activity.id} className="flex flex-wrap gap-x-3 gap-y-1 text-sm">
                    <span className="text-[#4A554E]">{activity.created_at ? new Date(activity.created_at).toLocaleDateString("ar-SA") : "—"}</span>
                    <strong>{activity.type_label || RECORD_LABELS[activity.type] || "عمل موثق"}</strong>
                    {activity.summary && <span className="text-[#4A554E]">{activity.summary}</span>}
                  </div>
                )) : <p className="text-sm text-[#4A554E]">لا توجد أعمال موثقة خلال الفترة.</p>}
              </div>
            )}
          </article>
        ))}
        {!busy && rows.length === 0 && <p className="text-sm text-[#4A554E]">لا توجد بيانات لعرضها في الفترة المحددة.</p>}
      </div>
    </section>
  );
}
